/**
 * Pure room geometry (no Pixi): axis-aligned rects, pieces, doors and gates.
 * A room is a union of walkable rects — one piece for a classic arena, a
 * chain of pieces joined by doors for an ADVANCE route (docs/01 room kinds).
 */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Travel direction through a piece (entry side → exit side). */
export type Dir = 'up' | 'down' | 'left' | 'right';

/** Unit vector of a direction. */
export function dirVec(dir: Dir): { x: number; y: number } {
  switch (dir) {
    case 'up': return { x: 0, y: -1 };
    case 'down': return { x: 0, y: 1 };
    case 'left': return { x: -1, y: 0 };
    default: return { x: 1, y: 0 };
  }
}

export function oppositeDir(dir: Dir): Dir {
  return dir === 'up' ? 'down' : dir === 'down' ? 'up' : dir === 'left' ? 'right' : 'left';
}

/** True for left/right travel (the door axis is x). */
export function isHorizontal(dir: Dir): boolean {
  return dir === 'left' || dir === 'right';
}

/**
 * A piece of a room: `outer` includes the wall band, `inner` is walkable.
 * `dir` orients the spawn frame (u along travel, v across) — formations and
 * slots written for arenas map "ahead of the player" through it.
 */
export interface Piece {
  readonly outer: Rect;
  readonly inner: Rect;
  readonly dir: Dir;
  /** Index in the room's piece list */
  readonly index: number;
  /**
   * Spawn-frame squeeze along travel: u → u0 + u·uSpan. Arenas/chambers use
   * (0, 1); route segments pull formations toward the ship's entry end so a
   * squad sits as close as it would in an arena.
   */
  readonly u0: number;
  readonly uSpan: number;
}

/**
 * A doorway through the bulkhead between piece `from` and piece `to`.
 * `rect` is walkable (it overlaps both pieces' inner rects); `gate` is the
 * barrier box in the middle of the bulkhead — solid while closed.
 */
export interface Door {
  readonly from: number;
  readonly to: number;
  readonly rect: Rect;
  readonly gate: Rect;
  /** Travel direction from `from` into `to` */
  readonly dir: Dir;
  closed: boolean;
  /** Seconds since the gate last opened (dissolve animation) / sealed */
  fadeT: number;
}

export function rectContains(r: Rect, x: number, y: number, margin = 0): boolean {
  return x > r.x - margin && x < r.x + r.w + margin && y > r.y - margin && y < r.y + r.h + margin;
}

export function rectCenter(r: Rect): { x: number; y: number } {
  return { x: r.x + r.w / 2, y: r.y + r.h / 2 };
}

/**
 * Squared displacement needed to fit a circle of radius `r` at (x, y) fully
 * inside `rect` (0 = already fits). Writes the fitted point into `out`.
 */
function fitInto(rect: Rect, x: number, y: number, r: number, out: { x: number; y: number }): number {
  const x0 = rect.x + r;
  const x1 = rect.x + rect.w - r;
  const y0 = rect.y + r;
  const y1 = rect.y + rect.h - r;
  // A rect thinner than the circle pins it to its center line
  const fx = x0 > x1 ? (x0 + x1) / 2 : x < x0 ? x0 : x > x1 ? x1 : x;
  const fy = y0 > y1 ? (y0 + y1) / 2 : y < y0 ? y0 : y > y1 ? y1 : y;
  out.x = fx;
  out.y = fy;
  return (fx - x) * (fx - x) + (fy - y) * (fy - y);
}

const fitScratch = { x: 0, y: 0 };

/**
 * Keep a circle inside the union of walkable rects: if it fits in any rect
 * it stays; otherwise it moves to the nearest fitting spot (walls between
 * unrelated rects are thick, so "nearest" is always the side it came from).
 * @returns true when the circle was moved
 */
export function clampToUnion(
  obj: { x: number; y: number }, r: number, rects: ReadonlyArray<Rect>,
): boolean {
  let bestD = Infinity;
  let bx = obj.x;
  let by = obj.y;
  for (const rect of rects) {
    const d = fitInto(rect, obj.x, obj.y, r, fitScratch);
    if (d === 0) return false;
    if (d < bestD) {
      bestD = d;
      bx = fitScratch.x;
      by = fitScratch.y;
    }
  }
  obj.x = bx;
  obj.y = by;
  return bestD !== Infinity;
}

/**
 * Push a circle out of a closed gate box along the door axis, toward the
 * side its center is on. @returns true when pushed
 */
export function pushOutOfGate(
  obj: { x: number; y: number }, r: number, door: Door,
): boolean {
  const g = door.gate;
  if (obj.x + r <= g.x || obj.x - r >= g.x + g.w || obj.y + r <= g.y || obj.y - r >= g.y + g.h) {
    return false;
  }
  if (isHorizontal(door.dir)) {
    const mid = g.x + g.w / 2;
    obj.x = obj.x < mid ? g.x - r : g.x + g.w + r;
  } else {
    const mid = g.y + g.h / 2;
    obj.y = obj.y < mid ? g.y - r : g.y + g.h + r;
  }
  return true;
}

/** Point (with radius) inside a closed gate box — bullets die on shields. */
export function hitsGate(door: Door, x: number, y: number, r: number): boolean {
  const g = door.gate;
  return x + r > g.x && x - r < g.x + g.w && y + r > g.y && y - r < g.y + g.h;
}

/**
 * Map spawn-frame fractions (u along travel, v across) to world space in
 * `piece`'s walkable rect. Arenas use 'right', so u = x and v = y as before.
 */
export function framePoint(
  piece: Piece, uIn: number, v: number, out: { x: number; y: number },
): void {
  const r = piece.outer;
  const u = piece.u0 + uIn * piece.uSpan;
  switch (piece.dir) {
    case 'right': out.x = r.x + u * r.w; out.y = r.y + v * r.h; break;
    case 'left': out.x = r.x + r.w - u * r.w; out.y = r.y + (1 - v) * r.h; break;
    case 'down': out.x = r.x + (1 - v) * r.w; out.y = r.y + u * r.h; break;
    default: out.x = r.x + v * r.w; out.y = r.y + r.h - u * r.h; break;
  }
}
